import { DataScopesEnum, NoteTypes, StrategyTypes, UserRightsEnum } from './Constants';

import Authorization from '../stores/Authorization';
import { INoteModel } from '../interfaces/INote';
import { IUserModel } from '../interfaces/IUser';

export default class NoteAuthHelper {
    static canUserViewNote = (
        currentNote: INoteModel,
        user: IUserModel,
        currentZoneGuid: string
    ): boolean => {
        const userRight = NoteAuthHelper.getViewNoteRight(
            currentNote.type?.id ?? 0,
            currentNote.originStrategyType
        );
        return Authorization.userHasRight(userRight, user, currentZoneGuid);
    };

    static canUserCreateNote = (
        user: IUserModel,
        currentZoneGuid: string,
        noteTypeId: number,
        strategyTypeId?: number
    ): boolean => {
        const userRight = NoteAuthHelper.getCreateNoteRight(noteTypeId, strategyTypeId);
        return Authorization.userHasRight(userRight, user, currentZoneGuid);
    };

    static canUserEditNote = (
        currentNote: INoteModel,
        user: IUserModel,
        currentZoneGuid: string
    ): boolean => {
        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
            user,
            currentZoneGuid
        );

        const userRight = NoteAuthHelper.getEditNoteRight(
            currentNote.type?.id ?? 0,
            currentNote.originStrategyType
        );

        const hasRight = Authorization.userHasRight(userRight, user, currentZoneGuid);

        return (
            hasRight &&
            (loggedInUserDataScopeId !== DataScopesEnum.LocalBasic.Value ||
                !!currentNote.allowLocalEdit)
        );
    };

    static canUserDeleteNote = (
        currentNote: INoteModel,
        user: IUserModel,
        currentZoneGuid: string
    ): boolean => {
        const userRight = NoteAuthHelper.getDeleteNoteRight(
            currentNote.type?.id ?? 0,
            currentNote.originStrategyType
        );
        return Authorization.userHasRight(userRight, user, currentZoneGuid);
    };

    static getViewNoteRight = (noteTypeId: number, strategyTypeId?: number): number => {
        switch (noteTypeId) {
            case NoteTypes.CaseNote:
                return UserRightsEnum.ViewCaseNotes;
            case NoteTypes.ZoneNote:
                return UserRightsEnum.ViewZoneNotes;
            case NoteTypes.PackageSettlementNote:
                return UserRightsEnum.ViewPackageDealNote;
            case NoteTypes.StrategySignificantChange:
            case NoteTypes.StrategyNote:
                switch (strategyTypeId) {
                    case StrategyTypes.StrategicPlan.Value:
                        return UserRightsEnum.ViewStrategicPlan;
                    case StrategyTypes.ExecutionPlan.Value:
                        return UserRightsEnum.ViewExecutionPlan;
                    case StrategyTypes.PlaintiffsFirm.Value:
                        return UserRightsEnum.ViewPlaintiffsFirmStrategyNote;
                    case StrategyTypes.Jurisdiction.Value:
                        return UserRightsEnum.ViewJurisdictionStrategyNote;
                    case StrategyTypes.Projects.Value:
                        return UserRightsEnum.ViewProjectsStrategyNote;
                    case StrategyTypes.LocalCounsel.Value:
                        return UserRightsEnum.ViewLocalCounselStrategyNote;
                    case StrategyTypes.Experts.Value:
                        return UserRightsEnum.ViewExpertStrategyNote;
                    default:
                        break;
                }
                break;
            default:
                break;
        }
        return 0;
    };

    static getCreateNoteRight = (noteTypeId: number, strategyTypeId?: number): number => {
        switch (noteTypeId) {
            case NoteTypes.CaseNote:
                return UserRightsEnum.AddCaseNote;
            case NoteTypes.ZoneNote:
                return UserRightsEnum.AddZoneNote;
            case NoteTypes.PackageSettlementNote:
                return UserRightsEnum.AddPackageDealNote;
            case NoteTypes.StrategySignificantChange:
            case NoteTypes.StrategyNote:
                switch (strategyTypeId) {
                    case StrategyTypes.StrategicPlan.Value:
                        return UserRightsEnum.EditStrategicPlan;
                    case StrategyTypes.ExecutionPlan.Value:
                        return UserRightsEnum.EditExecutionPlan;
                    case StrategyTypes.PlaintiffsFirm.Value:
                        return UserRightsEnum.AddPlaintiffsFirmStrategyNote;
                    case StrategyTypes.Jurisdiction.Value:
                        return UserRightsEnum.AddJurisdictionStrategyNote;
                    case StrategyTypes.Projects.Value:
                        return UserRightsEnum.AddProjectsStrategyNote;
                    case StrategyTypes.LocalCounsel.Value:
                        return UserRightsEnum.AddLocalCounselStrategyNote;
                    case StrategyTypes.Experts.Value:
                        return UserRightsEnum.AddExpertStrategyNote;
                    default:
                        break;
                }
                break;
            default:
                break;
        }
        return 0;
    };

    static getEditNoteRight = (noteTypeId: number, strategyTypeId?: number): number => {
        switch (noteTypeId) {
            case NoteTypes.CaseNote:
                return UserRightsEnum.EditCaseNote;
            case NoteTypes.ZoneNote:
                return UserRightsEnum.EditZoneNote;
            case NoteTypes.PackageSettlementNote:
                return UserRightsEnum.UpdatePackageDealNote;
            case NoteTypes.StrategySignificantChange:
            case NoteTypes.StrategyNote:
                switch (strategyTypeId) {
                    case StrategyTypes.StrategicPlan.Value:
                        return UserRightsEnum.EditStrategicPlan;
                    case StrategyTypes.ExecutionPlan.Value:
                        return UserRightsEnum.EditExecutionPlan;
                    case StrategyTypes.PlaintiffsFirm.Value:
                        return UserRightsEnum.UpdatePlaintiffsFirmStrategyNote;
                    case StrategyTypes.Jurisdiction.Value:
                        return UserRightsEnum.UpdateJurisdictionStrategyNote;
                    case StrategyTypes.Projects.Value:
                        return UserRightsEnum.UpdateProjectsStrategyNote;
                    case StrategyTypes.LocalCounsel.Value:
                        return UserRightsEnum.UpdateLocalCounselStrategyNote;
                    case StrategyTypes.Experts.Value:
                        return UserRightsEnum.UpdateExpertStrategyNote;
                    default:
                        break;
                }
                break;
            default:
                break;
        }

        return 0;
    };

    static getDeleteNoteRight = (noteTypeId: number, strategyTypeId?: number): number => {
        switch (noteTypeId) {
            case NoteTypes.CaseNote:
                return UserRightsEnum.DeleteCaseNote;
            case NoteTypes.ZoneNote:
                return UserRightsEnum.DeleteZoneNote;
            case NoteTypes.PackageSettlementNote:
                return UserRightsEnum.DeletePackageDealNote;
            case NoteTypes.StrategyNote:
                switch (strategyTypeId) {
                    case StrategyTypes.StrategicPlan.Value:
                        return UserRightsEnum.EditStrategicPlan;
                    case StrategyTypes.ExecutionPlan.Value:
                        return UserRightsEnum.EditExecutionPlan;
                    case StrategyTypes.PlaintiffsFirm.Value:
                        return UserRightsEnum.DeletePlaintiffsFirmStrategyNote;
                    case StrategyTypes.Jurisdiction.Value:
                        return UserRightsEnum.DeleteJurisdictionStrategyNote;
                    case StrategyTypes.Projects.Value:
                        return UserRightsEnum.DeleteProjectsStrategyNote;
                    case StrategyTypes.LocalCounsel.Value:
                        return UserRightsEnum.DeleteLocalCounselStrategyNote;
                    case StrategyTypes.Experts.Value:
                        return UserRightsEnum.DeleteExpertStrategyNote;
                    default:
                        break;
                }
                break;
            default:
                break;
        }

        return 0;
    };
}
